import {
  BaseLandingPageBlock,
  LandingPageBlock,
  CTASimpleCentered,
  CTASplitWithImage,
  CTATwoColumnWithImage,
  HeaderSimple,
  HeaderWithBackgroundImage,
  HeaderWithCards,
  HeroCentered,
  HeroWithRightImage,
  HeroWithScreenshot,
  StatsSimple,
  StatsGrid,
  StatsWithImage,
  LandingPageBlockTypes,
  FAQ,
  TestimonialSimpleCentered,
  FeatureTwoByTwoGrid,
  FeatureWithCTAs,
  FeatureTwoTextColumns,
  UnknownBlockType,
  InvalidProperties,
  ContentWithImage,
  ContentWithImageQuoteCTA,
  NewsletterSignup,
  HeroNewsletterSignup,
  FeatureWithDemo,
  HeroWithDemo,
  WysiwygBlock,
  YoutubeEmbedACFBlock,
  TaggedPostsACFBlock,
} from "./schemas";

const isLandingPageBlockWithFieldName = <T extends BaseLandingPageBlock>(
  block: LandingPageBlock,
  fieldName: T["acfFcLayout"]
): boolean =>
  block &&
  typeof block === "object" &&
  "acfFcLayout" in block &&
  block.acfFcLayout === fieldName;

export const isContentWithImageBlock = (
  block: LandingPageBlock
): block is ContentWithImage =>
  isLandingPageBlockWithFieldName<ContentWithImage>(
    block,
    LandingPageBlockTypes.ContentWithImage
  );

export const isContentWithImageQuoteCTA = (
  block: LandingPageBlock
): block is ContentWithImageQuoteCTA =>
  isLandingPageBlockWithFieldName<ContentWithImageQuoteCTA>(
    block,
    LandingPageBlockTypes.ContentWithImageQuoteCTA
  );

export const isCTASimpleCenteredBlock = (
  block: LandingPageBlock
): block is CTASimpleCentered =>
  isLandingPageBlockWithFieldName<CTASimpleCentered>(
    block,
    LandingPageBlockTypes.CTASimpleCentered
  );

export const isCTASplitWithImage = (
  block: LandingPageBlock
): block is CTASplitWithImage =>
  isLandingPageBlockWithFieldName<CTASplitWithImage>(
    block,
    LandingPageBlockTypes.CTASplitWithImage
  );

export const isCTATwoColumnWithImage = (
  block: LandingPageBlock
): block is CTATwoColumnWithImage =>
  isLandingPageBlockWithFieldName<CTATwoColumnWithImage>(
    block,
    LandingPageBlockTypes.CTATwoColumnWithImage
  );

export const isHeaderSimple = (
  block: LandingPageBlock
): block is HeaderSimple =>
  isLandingPageBlockWithFieldName<HeaderSimple>(
    block,
    LandingPageBlockTypes.HeaderSimple
  );

export const isHeaderWithBackgroundImage = (
  block: LandingPageBlock
): block is HeaderWithBackgroundImage =>
  isLandingPageBlockWithFieldName<HeaderWithBackgroundImage>(
    block,
    LandingPageBlockTypes.HeaderWithBackgroundImage
  );

export const isHeaderWithCards = (
  block: LandingPageBlock
): block is HeaderWithCards =>
  isLandingPageBlockWithFieldName<HeaderWithCards>(
    block,
    LandingPageBlockTypes.HeaderWithCards
  );

export const isHeroCentered = (
  block: LandingPageBlock
): block is HeroCentered =>
  isLandingPageBlockWithFieldName<HeroCentered>(
    block,
    LandingPageBlockTypes.HeroCentered
  );

export const isHeroNewsletterSignup = (
  block: LandingPageBlock
): block is HeroNewsletterSignup =>
  isLandingPageBlockWithFieldName<HeroNewsletterSignup>(
    block,
    LandingPageBlockTypes.HeroNewsletterSignup
  );

export const isHeroWithDemo = (
  block: LandingPageBlock
): block is HeroWithDemo =>
  isLandingPageBlockWithFieldName<HeroWithDemo>(
    block,
    LandingPageBlockTypes.HeroWithDemo
  );

export const isHeroWithRightImage = (
  block: LandingPageBlock
): block is HeroWithRightImage =>
  isLandingPageBlockWithFieldName<HeroWithRightImage>(
    block,
    LandingPageBlockTypes.HeroWithRightImage
  );

export const isHeroWithScreenshot = (
  block: LandingPageBlock
): block is HeroWithScreenshot =>
  isLandingPageBlockWithFieldName<HeroWithScreenshot>(
    block,
    LandingPageBlockTypes.HeroWithScreenshot
  );

export const isStatsSimple = (block: LandingPageBlock): block is StatsSimple =>
  isLandingPageBlockWithFieldName<StatsSimple>(
    block,
    LandingPageBlockTypes.StatsSimple
  );

export const isStatsGrid = (block: LandingPageBlock): block is StatsGrid =>
  isLandingPageBlockWithFieldName<StatsGrid>(
    block,
    LandingPageBlockTypes.StatsGrid
  );

export const isStatsWithImage = (
  block: LandingPageBlock
): block is StatsWithImage =>
  isLandingPageBlockWithFieldName<StatsWithImage>(
    block,
    LandingPageBlockTypes.StatsWithImage
  );

export const isFAQ = (block: LandingPageBlock): block is FAQ =>
  isLandingPageBlockWithFieldName<FAQ>(block, LandingPageBlockTypes.FAQ);

export const isTestimonialSimpleCentered = (
  block: LandingPageBlock
): block is TestimonialSimpleCentered =>
  isLandingPageBlockWithFieldName<TestimonialSimpleCentered>(
    block,
    LandingPageBlockTypes.TestimonialSimpleCentered
  );

export const isFeatureWithDemo = (
  block: LandingPageBlock
): block is FeatureWithDemo =>
  isLandingPageBlockWithFieldName<FeatureWithDemo>(
    block,
    LandingPageBlockTypes.FeatureWithDemo
  );

export const isFeatureTwoByTwoGrid = (
  block: LandingPageBlock
): block is FeatureTwoByTwoGrid =>
  isLandingPageBlockWithFieldName<FeatureTwoByTwoGrid>(
    block,
    LandingPageBlockTypes.FeatureTwoByTwoGrid
  );

export const isFeatureTwoTextColumns = (
  block: LandingPageBlock
): block is FeatureTwoTextColumns =>
  isLandingPageBlockWithFieldName<FeatureTwoTextColumns>(
    block,
    LandingPageBlockTypes.FeatureTwoTextColumns
  );

export const isFeatureWithCTAs = (
  block: LandingPageBlock
): block is FeatureWithCTAs =>
  isLandingPageBlockWithFieldName<FeatureWithCTAs>(
    block,
    LandingPageBlockTypes.FeatureWithCTAs
  );

export const isUnknownBlockType = (
  block: LandingPageBlock
): block is UnknownBlockType =>
  isLandingPageBlockWithFieldName<UnknownBlockType>(
    block,
    LandingPageBlockTypes.UnknownBlockType
  );

export const isInvalidPropertiesError = (
  block: LandingPageBlock
): block is InvalidProperties =>
  isLandingPageBlockWithFieldName<InvalidProperties>(
    block,
    LandingPageBlockTypes.InvalidProperties
  );
export const isNewsletterSignup = (
  block: LandingPageBlock
): block is NewsletterSignup =>
  isLandingPageBlockWithFieldName<NewsletterSignup>(
    block,
    LandingPageBlockTypes.NewsletterSignup
  );
export const isWysiwygBlock = (
  block: LandingPageBlock
): block is WysiwygBlock =>
  isLandingPageBlockWithFieldName<WysiwygBlock>(
    block,
    LandingPageBlockTypes.Wysiwyg
  );

export const isYoutubeEmbedBlock = (
  block: LandingPageBlock
): block is YoutubeEmbedACFBlock =>
  isLandingPageBlockWithFieldName<YoutubeEmbedACFBlock>(
    block,
    LandingPageBlockTypes.YoutubeEmbed
  );

export const isTaggedPostsBlock = (
  block: LandingPageBlock
): block is TaggedPostsACFBlock =>
  isLandingPageBlockWithFieldName<TaggedPostsACFBlock>(
    block,
    LandingPageBlockTypes.TaggedPosts
  );
